import { Q, Query } from "@nozbe/watermelondb";
import { useDatabase } from "@nozbe/watermelondb/hooks";
import { useCallback, useEffect, useState } from "react";
import SerialNumber from "../data/model/SerialNumber";
import { OfflineSearchOptions, useOfflineQuery } from "../offline-search";
import { DEFAULT_SEARCH_LIMIT } from "../offline-search/constants";
import Product from "../data/model/Product";
import { useWithAppState } from "@towersystems/roam-common-native";
import { ProductLocationInventoryPricing } from "@towersystems/roam-common/lib/generated-types";

export const useMakeBaseSerialNumberForProductQuery = () => {
  const database = useDatabase();

  const serialNumberCollection =
    database.collections.get<SerialNumber>("serial_numbers");

  const makeBaseSerialNumberForProductQuery = useCallback(
    (
      productLocationInventoryPricingId: string,
      term: string
    ): Query<SerialNumber> => {
      const sanitizedTerm = Q.sanitizeLikeString(term);

      const query = serialNumberCollection.query(
        Q.where("value", Q.like(`%${sanitizedTerm}%`)),
        Q.and(
          Q.where(
            "productLocationInventoryPricingId",
            Q.eq(productLocationInventoryPricingId)
          )
        )
      );

      return query;
    },
    []
  );

  return {
    makeBaseSerialNumberForProductQuery,
  };
};

export const useOfflineSerialNumberForProductSearch = (productId: string) => {
  const { doQuery, ...result } = useOfflineQuery<SerialNumber>();
  const { makeBaseSerialNumberForProductQuery } =
    useMakeBaseSerialNumberForProductQuery();

  const database = useDatabase();

  const [productLocationInventoryPricing, setProductLocationInventoryPricing] =
    useState<ProductLocationInventoryPricing | undefined>(undefined);

  const { appState } = useWithAppState();

  const searchSerialNumbersForProduct = useCallback(
    async (term: string, options?: OfflineSearchOptions) => {
      if (!productLocationInventoryPricing) {
        return Promise.reject("No product location inventory pricing found");
      }

      const qb = makeBaseSerialNumberForProductQuery(
        productLocationInventoryPricing?.id,
        term
      );

      const extendedQuery = qb.extend(
        Q.sortBy("value", Q.asc),
        Q.take(options?.limit || DEFAULT_SEARCH_LIMIT)
      );

      return doQuery(extendedQuery);
    },
    [productLocationInventoryPricing]
  );

  async function fetchProductLocationInventoryPricing(
    targetProductId: string,
    targetLocationId: string
  ) {
    const product = await database.collections
      .get<Product>("products")
      .find(targetProductId);

    const lip = product.locationInventoryPricing.find(
      (lip) => lip.locationId === targetLocationId
    );

    if (!lip) {
      throw Error("No location inventory pricing found for product");
    }

    return lip;
  }

  useEffect(() => {
    fetchProductLocationInventoryPricing(productId, appState.locationId)
      .then((entity) => {
        setProductLocationInventoryPricing(entity);
      })
      .catch((error) => {
        console.log(error);
        setProductLocationInventoryPricing(undefined);
      });
  }, [productId, appState.locationId]);

  return {
    searchSerialNumbersForProduct,
    ...result,
  };
};
